import {
    ActivityTargetView,
    formatTourDate,
    formatTourTime,
    isTourDateTimeInPast,
} from "@sp-crm/core";

const cadenceNounByUnit: Record<string, string> = {
    Daily: "day",
    Weekly: "week",
    Monthly: "month",
};

export const describeCadence = (intervalCount: number, intervalUnit: string): string => {
    const noun = cadenceNounByUnit[intervalUnit] ?? intervalUnit.toLowerCase();
    return intervalCount === 1 ? `every ${noun}` : `every ${intervalCount} ${noun}s`;
};

export type PendingVisitView = {
    kind: "scheduled" | "awaitingOutcome";
    eventDate: string;
    eventTime: string | null;
} | null;

export type LoosePendingVisit = {
    kind: "scheduled" | "awaitingOutcome";
    eventDate: string;
    eventTime?: string | null;
} | null;

export const normalizePendingVisit = (
    pendingVisit: LoosePendingVisit | undefined,
): PendingVisitView =>
    pendingVisit
        ? {
              kind: pendingVisit.kind,
              eventDate: pendingVisit.eventDate,
              eventTime: pendingVisit.eventTime ?? null,
          }
        : null;

export const reconcilePendingVisitKind = (
    pendingVisit: PendingVisitView,
    now: Date,
): PendingVisitView =>
    pendingVisit
        ? {
              ...pendingVisit,
              kind: isTourDateTimeInPast(
                  pendingVisit.eventDate,
                  pendingVisit.eventTime,
                  now,
              )
                  ? "awaitingOutcome"
                  : "scheduled",
          }
        : pendingVisit;

export interface TouchpointStatusView {
    state: "due" | "satisfied" | "snoozed";
    pendingVisit: PendingVisitView;
    lastContactedAt: string | null;
    lastContactedActivity: ActivityTargetView | null;
    nextDueAt: string | null;
}

const completedWithTarget = (
    date: string,
    target: ActivityTargetView | null,
): string | null => (target ? `${target.label} completed ${date}` : null);

export const touchpointStatusLabel = (status: TouchpointStatusView): string => {
    if (status.state === "snoozed") {
        return "Snoozed";
    }
    const date = status.lastContactedAt
        ? (formatTourDate(status.lastContactedAt) ?? status.lastContactedAt)
        : null;
    if (status.state === "satisfied") {
        if (status.pendingVisit?.kind === "scheduled") {
            const { eventDate, eventTime } = status.pendingVisit;
            const visitDate = formatTourDate(eventDate) ?? eventDate;
            return `Visit scheduled for ${visitDate}${formatTourTime(eventTime)}`;
        }
        if (status.pendingVisit?.kind === "awaitingOutcome") {
            const { eventDate, eventTime } = status.pendingVisit;
            const visitDate = formatTourDate(eventDate) ?? eventDate;
            return `Visit awaiting outcome from ${visitDate}${formatTourTime(eventTime)}`;
        }
        const completed = date
            ? (completedWithTarget(date, status.lastContactedActivity) ??
              `Completed ${date}`)
            : "Completed";
        const nextDueDate = status.nextDueAt
            ? (formatTourDate(status.nextDueAt) ?? status.nextDueAt)
            : null;
        return nextDueDate ? `${completed} · next due ${nextDueDate}` : completed;
    }
    if (!date) {
        return "Not yet completed";
    }
    return (
        completedWithTarget(date, status.lastContactedActivity) ??
        `Last completed ${date}`
    );
};

export const describeTargets = (targets: ActivityTargetView[]): string => {
    const labels = targets.map(target => target.label);
    if (labels.length <= 1) {
        return labels[0] ?? "";
    }
    return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
};
